import * as React from 'react';
import { t, tryT, type MessageKey } from '@reos/i18n';
import { DomainApiError } from '../lib/api-error.js';
import { captionOrPreparing } from '../lib/broker-text.js';
import { fetchOwnCaseIds } from '../lib/cases-api.js';
import { useSession } from '../stores/session.js';
import { uploadHumanTaskDocument } from '../lib/human-tasks-api.js';
import {
  answerResolveUnknown,
  listResolveUnknown,
  RESOLVE_UNKNOWN_PAGE_LIMIT,
  type ResolveUnknownFilter,
  type ResolveUnknownTask,
} from '../lib/resolve-unknown-api.js';

/**
 * `/k-doplneni` (krok H1) — FRONTA „K DOPLNENI" NAPRIC ZAKAZKAMI.
 *
 * Rozhodnuti R.B. 26. 9. 2026: kde server odpoved nenajde, prevezme ji clovek.
 * Tahle stranka je to misto, kde se to dela - a soucasne misto, kde se behem
 * pilotu laadi, co server jeste neumi (prehled dole, bod 4 zadani).
 *
 * ZADNY HARDCODED TEXT (zlate pravidlo 5): vsechno jde pres
 * `webAdmin.resolveUnknown.*`, `dd.human.resolveUnknown.*`, `dd.report.*`.
 * Otazka se tiskne TYMZ textem jako v dokumentu (`dd.report.question.*`), takze
 * clovek ve fronte cte presne to, co uvidi zakaznik.
 */

const cs = { locale: 'cs' } as const;

function tr(key: string, values?: Record<string, unknown>): string {
  return t(key as MessageKey, values === undefined ? cs : { ...cs, values });
}

/**
 * Klice se skladaji z runtime hodnot (klic otazky, kod duvodu), takze je nejde
 * overit typem. Chybejici preklad NENI chyba stranky - fronta musi zustat
 * pouzitelna i pro otazku, kterou katalog jeste nezna.
 */
function tryTr(key: string, values?: Record<string, unknown>): string | undefined {
  return tryT(key as MessageKey, values === undefined ? cs : { ...cs, values });
}

const CASE_KINDS = ['LAND', 'HOUSE', 'FLAT'] as const;
const ANSWERS = ['YES', 'NO', 'NOT_FOUND'] as const;
const KNOWLEDGE_KEYS = [
  'planning_documents_url',
  'municipality_website',
  'utility_operator_url',
] as const;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function waitingDays(createdAt: string): number {
  const created = new Date(createdAt).getTime();
  if (!Number.isFinite(created)) return 0;
  return Math.max(0, Math.floor((Date.now() - created) / MS_PER_DAY));
}

/** Napoveda „kde hledat" z `hints` (JSONB) - cte se obranne, tvar neni kontrakt. */
function hintUrls(task: ResolveUnknownTask): readonly string[] {
  const hints = (task.hints ?? {}) as { urls?: unknown; reasonLink?: unknown };
  const urls = Array.isArray(hints.urls)
    ? hints.urls.filter((u): u is string => typeof u === 'string')
    : [];
  const link = typeof hints.reasonLink === 'string' ? [hints.reasonLink] : [];
  return [...new Set([...link, ...urls, ...(task.url === '' ? [] : [task.url])])];
}

function hintText(task: ResolveUnknownTask): string {
  const hints = (task.hints ?? {}) as { municipalityName?: unknown; parcels?: unknown };
  const parts: string[] = [];
  if (typeof hints.municipalityName === 'string' && hints.municipalityName !== '') {
    parts.push(hints.municipalityName);
  }
  if (Array.isArray(hints.parcels)) {
    const parcels = hints.parcels.filter((p): p is string => typeof p === 'string');
    if (parcels.length > 0) parts.push(parcels.join(', '));
  }
  return parts.join(' · ');
}

/**
 * Krok OPS1 bod 4 — U REGISTRU O OSOBE SE ODPOVIDA JINYMI SLOVY.
 *
 * Do prichodu klicu k CEE (a u ISIR vsude, kde politika stroji dotaz zakazuje)
 * lustruje exekuce a insolvence CLOVEK rucne. „Ano / Ne" je u takove otazky
 * zradne: obsluha nevi, jestli „Ne" znamena „nenasel jsem zadny zaznam" nebo
 * „nepodarilo se mi to overit". Proto u zdroju `cee.*` a `isir.*` ta sama
 * trojice odpovedi dostane presny popisek („Záznam nalezen" / „Ověřeno ručně —
 * bez záznamu" / „Nedohledáno ani ručně"). HODNOTY, ktere jdou na server, se
 * nemeni - je to jen popisek, takze report pise „ověřeno ručně dne …" uz
 * stavajici cestou (`HUMAN_VERIFIED_SOURCE_ID`).
 */
function isPersonRegistrySource(sourceId: string): boolean {
  return sourceId.startsWith('cee.') || sourceId.startsWith('isir.');
}

function answerLabel(task: ResolveUnknownTask, answer: string): string {
  const base = `dd.human.resolveUnknown.${isPersonRegistrySource(task.sourceId) ? 'answerRegistry' : 'answer'}`;
  // Chybejici popisek registru nesmi vyprazdnit nabidku - padne se na obecny.
  return tryTr(`${base}.${answer}`) ?? tr(`dd.human.resolveUnknown.answer.${answer}`);
}

function questionLabel(task: ResolveUnknownTask): string {
  // Krok UX1: kdyz katalog popisek otazky nema, neukazujeme `q-owner` ani
  // `Q-PLAN-03` — makleri napise „Text se pripravuje“ (viz lib/broker-text).
  return captionOrPreparing(tryTr(`dd.report.question.${task.questionKey}`), task.questionKey);
}

function reasonLabel(task: ResolveUnknownTask): string {
  if (task.reasonCode === null) return '';
  return (
    captionOrPreparing(
      tryTr(`dd.report.reason.${task.reasonCode}`, {
        named: 'NO',
        sources: '',
        detailed: 'NO',
        detail: '',
      }),
      task.reasonCode,
    )
  );
}

function TaskRow({
  task,
  onDone,
}: {
  task: ResolveUnknownTask;
  onDone: () => void;
}): React.JSX.Element {
  const [answer, setAnswer] = React.useState<(typeof ANSWERS)[number]>('YES');
  const [evidenceUrl, setEvidenceUrl] = React.useState('');
  const [note, setNote] = React.useState('');
  const [knowledgeKey, setKnowledgeKey] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  /**
   * Krok NAP8 bod 3 (A20) — NAHRANI DOKLADU U UKOLU CEE / ISIR.
   *
   * Dotaz do CEE i rucni lustrace v ISIR konci LISTINOU (PDF z CEE, snimek
   * vysledku z ISIR). Do NAP8 ji ve fronte nebylo kde nechat: `uploadHuman
   * TaskDocument` existoval, ale volal ho jen panel zakazky — a ten u ukolu
   * nad OTAZKOU (`RESOLVE_UNKNOWN`) nahravani zamerne neukazuje, protoze se
   * odpovida tady. Tlacitko je proto tady, u te same odpovedi.
   *
   * Nahrani NEPREPISUJE odpoved: podklad je dukaz, odpoved zustava na
   * cloveku (vyber nize). U strojoveho zdroje (`isir.ws.osoba`) je podklad
   * jen PRILOHA ukolu — server to vraci a hlaska to rika.
   */
  async function upload(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await uploadHumanTaskDocument(task.caseId, task.id, file);
      setNotice(
        result.attachmentOnly
          ? t('webAdmin.resolveUnknown.uploadAttachmentOnly', cs)
          : t('webAdmin.resolveUnknown.uploadDone', cs),
      );
    } catch (cause) {
      setError(cause instanceof DomainApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await answerResolveUnknown(task.caseId, task.id, {
        answer,
        ...(evidenceUrl === '' ? {} : { evidenceUrl }),
        ...(note === '' ? {} : { note }),
        ...(knowledgeKey === ''
          ? {}
          : { knowledgeKey: knowledgeKey as (typeof KNOWLEDGE_KEYS)[number] }),
      });
      setNotice(
        result.knowledgeStored
          ? t('webAdmin.resolveUnknown.form.savedKnowledge', cs)
          : t('webAdmin.resolveUnknown.form.saved', cs),
      );
      onDone();
    } catch (cause) {
      setError(cause instanceof DomainApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="border-t py-3"
      data-testid="resolve-unknown-task"
      data-task-id={task.id}
      data-question-key={task.questionKey}
      data-source-id={task.sourceId}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <strong>{questionLabel(task)}</strong>
        <span className="text-xs text-gray-500">
          {tr(`webAdmin.resolveUnknown.caseKind.${task.caseKind}`)} ·{' '}
          {t('webAdmin.resolveUnknown.waitingDays', {
            ...cs,
            values: { days: String(waitingDays(task.createdAt)) },
          })}
        </span>
      </div>
      <p className="my-1 text-xs text-gray-600">{reasonLabel(task)}</p>
      {hintText(task) !== '' && <p className="my-1 text-xs text-gray-600">{hintText(task)}</p>}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <a
          className="underline"
          href={`/zakazky/${task.caseId}`}
          data-testid="resolve-unknown-case-link"
        >
          {t('webAdmin.resolveUnknown.openCase', cs)}
        </a>
        {hintUrls(task).map((url) => (
          <a
            key={url}
            className="underline"
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="resolve-unknown-source-link"
          >
            {t('webAdmin.resolveUnknown.openSource', cs)}
          </a>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
        <label>
          {t('webAdmin.resolveUnknown.form.answer', cs)}{' '}
          <select
            value={answer}
            disabled={busy}
            onChange={(event) => setAnswer(event.target.value as (typeof ANSWERS)[number])}
            data-testid="resolve-unknown-answer"
          >
            {ANSWERS.map((value) => (
              <option key={value} value={value}>
                {answerLabel(task, value)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('webAdmin.resolveUnknown.form.evidenceUrl', cs)}{' '}
          <input
            type="url"
            value={evidenceUrl}
            disabled={busy}
            onChange={(event) => setEvidenceUrl(event.target.value)}
            data-testid="resolve-unknown-evidence"
          />
        </label>
        <label>
          {t('webAdmin.resolveUnknown.form.note', cs)}{' '}
          <input
            type="text"
            value={note}
            disabled={busy}
            onChange={(event) => setNote(event.target.value)}
            data-testid="resolve-unknown-note"
          />
        </label>
        <label>
          {t('webAdmin.resolveUnknown.form.knowledge', cs)}{' '}
          <select
            value={knowledgeKey}
            disabled={busy}
            onChange={(event) => setKnowledgeKey(event.target.value)}
            data-testid="resolve-unknown-knowledge"
          >
            <option value="">{t('webAdmin.resolveUnknown.form.knowledgeNone', cs)}</option>
            {KNOWLEDGE_KEYS.map((key) => (
              <option key={key} value={key}>
                {tr(`dd.knowledge.key.${key}`)}
              </option>
            ))}
          </select>
        </label>
        {/* Podklad se nahrava jen u registru o OSOBE (CEE / ISIR) - u ostatnich
            otazek je odpovedi odkaz, ne listina. */}
        {isPersonRegistrySource(task.sourceId) && (
          <label>
            {t('webAdmin.resolveUnknown.uploadDocument', cs)}{' '}
            <input
              type="file"
              accept="image/png,application/pdf"
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file === undefined) return;
                void upload(file);
              }}
              data-testid="resolve-unknown-upload"
            />
          </label>
        )}
        <button
          type="button"
          className="rh-btn rh-btn--primary"
          disabled={busy}
          onClick={() => void submit()}
          data-testid="resolve-unknown-submit"
        >
          {t('webAdmin.resolveUnknown.form.submit', cs)}
        </button>
      </div>

      {error !== null && (
        <p role="alert" className="mt-1 text-xs text-red-700">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="mt-1 text-xs text-green-700" data-testid="resolve-unknown-notice">
          {notice}
        </p>
      )}
    </div>
  );
}

export function KDoplneniPage(): React.JSX.Element {
  const userId = useSession((s) => s.user?.id ?? null);
  const [items, setItems] = React.useState<readonly ResolveUnknownTask[] | null>(null);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<ResolveUnknownFilter>({});
  /** Vychozi zuzeni fronty na vlastni zakazky (ADM1 bod 6). */
  const [mineOnly, setMineOnly] = React.useState(true);
  const [ownCaseIds, setOwnCaseIds] = React.useState<readonly string[] | null>(null);
  const mineOnlyActive = mineOnly && userId !== null;
  const mineOnlyPending = mineOnlyActive && ownCaseIds === null;

  React.useEffect(() => {
    if (!mineOnlyActive) {
      setOwnCaseIds(null);
      return;
    }
    let cancelled = false;
    void fetchOwnCaseIds(userId).then(
      (ids) => {
        if (!cancelled) setOwnCaseIds(ids);
      },
      () => {
        if (!cancelled) setOwnCaseIds([]);
      },
    );
    return () => {
      cancelled = true;
    };
    // `mineOnlyActive` uz zahrnuje `userId !== null`, ale `userId` musi byt
    // ve zavislostech explicitne (bez toho by eslint-plugin-react-hooks hlasil chybu).
  }, [mineOnlyActive, userId]);

  const effectiveFilter = React.useMemo<ResolveUnknownFilter>(
    () => ({
      ...filter,
      ...(mineOnlyActive && ownCaseIds !== null ? { caseIds: ownCaseIds.join(',') } : {}),
    }),
    [filter, mineOnlyActive, ownCaseIds],
  );

  const load = React.useCallback(async (): Promise<void> => {
    // "Jen moje zakazky" je zapnute, ale seznam vlastnich zakazek jeste nedorazil -
    // pockat, at prvni strana nejde bez filtru (blikla by cela fronta).
    if (mineOnlyPending) return;
    try {
      /*
       * Krok FUNK1 bod 3: „Ladění pilotu" (statistiky fronty) se odsud
       * PRESUNULO do `/admin`. Fronta uz proto nevola
       * `getResolveUnknownStats()` — jedno volani API navic na kazde
       * nacteni fronty, ktere nikdo na teto obrazovce necte.
       */
      const page = await listResolveUnknown(effectiveFilter);
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setError(null);
    } catch (cause) {
      setError(
        cause instanceof DomainApiError ? cause.message : t('webAdmin.resolveUnknown.error', cs),
      );
    }
  }, [effectiveFilter, mineOnlyPending]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function loadMore(): Promise<void> {
    if (nextCursor === null) return;
    setLoadingMore(true);
    try {
      const page = await listResolveUnknown({ ...effectiveFilter, cursor: nextCursor });
      setItems((previous) => [...(previous ?? []), ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (cause) {
      setError(
        cause instanceof DomainApiError ? cause.message : t('webAdmin.resolveUnknown.error', cs),
      );
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="p-6" data-testid="k-doplneni-page">
      <h1 className="mb-1 text-xl font-bold">{t('webAdmin.resolveUnknown.heading', cs)}</h1>
      <p className="mb-4 text-sm text-gray-600">{t('webAdmin.resolveUnknown.hint', cs)}</p>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <label>
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(event) => setMineOnly(event.target.checked)}
            data-testid="resolve-unknown-filter-mine"
          />{' '}
          {t('webAdmin.resolveUnknown.filter.mine', cs)}
        </label>
        <label>
          {t('webAdmin.resolveUnknown.filter.caseKind', cs)}{' '}
          <select
            value={filter.caseKind ?? ''}
            onChange={(event) =>
              setFilter((previous) => ({
                ...previous,
                ...(event.target.value === ''
                  ? { caseKind: undefined }
                  : { caseKind: event.target.value as (typeof CASE_KINDS)[number] }),
              }))
            }
            data-testid="resolve-unknown-filter-kind"
          >
            <option value="">{t('webAdmin.resolveUnknown.filter.all', cs)}</option>
            {CASE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {tr(`webAdmin.resolveUnknown.caseKind.${kind}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('webAdmin.resolveUnknown.filter.source', cs)}{' '}
          <input
            type="text"
            value={filter.sourceId ?? ''}
            onChange={(event) =>
              setFilter((previous) => ({ ...previous, sourceId: event.target.value }))
            }
            data-testid="resolve-unknown-filter-source"
          />
        </label>
        <label>
          {t('webAdmin.resolveUnknown.filter.age', cs)}{' '}
          <input
            type="number"
            min={0}
            value={filter.olderThanDays === undefined ? '' : String(filter.olderThanDays)}
            onChange={(event) =>
              setFilter((previous) => ({
                ...previous,
                olderThanDays: event.target.value === '' ? undefined : Number(event.target.value),
              }))
            }
            data-testid="resolve-unknown-filter-age"
          />
        </label>
      </div>

      {error !== null && (
        <p role="alert" className="mb-2 text-red-600">
          {error}
        </p>
      )}

      {items === null ? (
        <p>{t('common.loading', cs)}</p>
      ) : items.length === 0 ? (
        <p data-testid="resolve-unknown-empty">{t('webAdmin.resolveUnknown.empty', cs)}</p>
      ) : (
        <div data-testid="resolve-unknown-queue">
          {items.map((task) => (
            <TaskRow key={task.id} task={task} onDone={() => void load()} />
          ))}
          {nextCursor !== null && (
            <button
              type="button"
              className="rh-btn mt-3"
              disabled={loadingMore}
              onClick={() => void loadMore()}
              data-testid="resolve-unknown-load-more"
            >
              {t('webAdmin.resolveUnknown.loadMore', {
                ...cs,
                values: { pageSize: String(RESOLVE_UNKNOWN_PAGE_LIMIT) },
              })}
            </button>
          )}
        </div>
      )}

    </div>
  );
}
